import { useMemo, useState } from 'react';
import { FilterCard, MetricCard, MetricGrid, SectionCard } from '@/components/DataList';
import { ScreenFrame } from '@/components/Screen';
import { Icon } from '@/components/icons';
import { admin } from '@/api/endpoints';
import { pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { Button, Empty, ErrorBox, Loading, ProSearch, Select } from '@/components/ui';
import { COMPANY_FILTER_OPTIONS, COMPANY_OPTIONS } from '@/components/panelOptions';
import { formatDateTime, str } from '@/utils/format';

const COMPANY_LABELS: Record<string, string> = Object.fromEntries(
  COMPANY_OPTIONS.map((o) => [o.value, o.label]),
);

export function SmsMessagesScreen() {
  const [company, setCompany] = useState('all');
  const [q, setQ] = useState('');
  const [applied, setApplied] = useState({ company: 'all', q: '' });

  const state = useAsync(
    () => admin.executorMessages({ company: applied.company, q: applied.q }),
    (p) => pickList(p, 'data', 'messages', 'items') as JsonMap[],
    [applied],
  );

  const rows = state.data ?? [];

  const syriatelCount = useMemo(() => rows.filter((r) => str(r.company) === 'syriatel').length, [rows]);
  const mtnCount = useMemo(() => rows.filter((r) => str(r.company) === 'mtn').length, [rows]);

  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="bell"
      title="آخر رسائل الشركات"
      subtitle="أرشفة آخر رسائل SMS القادمة من سيريتل و MTN فقط، لمراجعة إشعارات التحويل والدفع المهمة من لوحة الإدارة."
      actions={
        <Button type="button" variant="secondary" onClick={() => state.reload()}>
          <Icon name="refresh" size={18} /> تحديث
        </Button>
      }
    >
      <MetricGrid>
        <MetricCard title="الرسائل المعروضة" value={String(rows.length)} icon={<Icon name="bell" size={21} />} />
        <MetricCard title="سيريتل" value={String(syriatelCount)} icon={<Icon name="device" size={21} />} />
        <MetricCard title="MTN" value={String(mtnCount)} icon={<Icon name="device" size={21} />} />
      </MetricGrid>

      <FilterCard
        title="تصفية الرسائل"
        icon={<Icon name="settings" size={24} />}
        subtitle="تُسحب الرسائل من تطبيق منفذ USSD بعد منح صلاحية قراءة الرسائل على جهاز الإدارة."
      >
        <Select
          label="الشركة"
          value={company}
          onChange={setCompany}
          options={COMPANY_FILTER_OPTIONS}
        />
        <ProSearch
          value={q}
          onChange={setQ}
          placeholder="بحث بالمرسل أو نص الرسالة أو منفذ التنفيذ"
        />
        <Button type="button" onClick={() => setApplied({ company, q: q.trim() })} disabled={state.loading}>
          {state.loading ? 'جاري البحث...' : 'بحث'}
        </Button>
      </FilterCard>

      <SectionCard title={`الرسائل المحفوظة (${rows.length})`} icon={<Icon name="bell" size={24} />}>
        {rows.length === 0 ? (
          <Empty
            title="لا توجد رسائل محفوظة"
            hint="افتح تطبيق أندرويد لمنفذ USSD وامنح صلاحية قراءة الرسائل ثم حدّث القائمة."
          />
        ) : (
          <div className="sms-message-list">
            {rows.map((row) => (
              <article key={str(row.id)} className="sms-message-card">
                <div className="list-head">
                  <strong>
                    {COMPANY_LABELS[str(row.company)] ?? str(row.company)} · {str(row.sender)}
                  </strong>
                  <span>{formatDateTime(row.received_at ?? row.created_at)}</span>
                </div>
                <p>{str(row.body)}</p>
                <small className="muted">
                  المنفّذ: {str(row.executor_name ?? row.user_name, '—')}
                  {str(row.executor_phone ?? row.device_uid, '')
                    ? ` · ${str(row.executor_phone ?? row.device_uid)}`
                    : ''}
                </small>
              </article>
            ))}
          </div>
        )}
      </SectionCard>
    </ScreenFrame>
  );
}
